import cn from 'classnames';
import React from 'react';

import updateConditionChildPositionOperation from 'editor/src/store/design/operation/updateConditionChildPositionOperation';
import getConditionChildrenKey from 'editor/src/store/design/reducer/getConditionChildrenKey';
import { Condition, ConditionChild, ConditionGroup, conditionGroupChildenKey } from 'editor/src/store/design/types';
import { groupPersonalizationFields } from 'editor/src/store/design/util/personalizationFieldGroups';
import { useDispatch } from 'editor/src/store/hooks';

import useTr from 'editor/src/util/useTr';

import PersonalizationCondition from 'editor/src/component/PersonalizationContent/PersonalizationElements/PersonalizationCondition';
import PersonalizationImage from 'editor/src/component/PersonalizationContent/PersonalizationElements/PersonalizationImage';
import PersonalizationText from 'editor/src/component/PersonalizationContent/PersonalizationElements/PersonalizationText';

import PersonalizationShape from '../../PersonalizationContent/PersonalizationElements/PersonalizationShape';
import FieldGroupSettings from '../FieldGroupSettings';

import ReorganizableElement from './ReorganizableList/Element';
import ReorganizableList from './ReorganizableList/List';

import styles from './index.module.scss';

interface Props {
  isEditing: boolean;
  spreadIndex: number;
  conditionGroup: ConditionGroup;
  parentCondition: Condition | undefined;
  elements: ConditionChild[];
  textOnly: boolean;
  onConditionCreated?: (conditionId: string, edit: () => void) => void;
  className?: string;
}

function ConditionElements({
  spreadIndex,
  conditionGroup,
  elements,
  parentCondition,
  isEditing,
  onConditionCreated,
  textOnly,
  className,
}: Props) {
  const dispatch = useDispatch();
  const tr = useTr();

  const childrenKey = parentCondition
    ? getConditionChildrenKey(parentCondition.id, parentCondition.activeOptionId)
    : conditionGroupChildenKey;
  const sections = groupPersonalizationFields(conditionGroup, elements, childrenKey);
  const ungrouped = sections
    .flatMap((section) => (section.group ? [] : [section.element]))
    .filter((element) => !textOnly || element.type === 'text');
  const renderFields = (fields: ConditionChild[]) => {
    const visibleElements = fields.filter((element) => !textOnly || element.type === 'text');
    return (
      <ReorganizableList
        className={cn(styles.ConditionElements)}
        margin={8}
        onIndexUpdate={(prevIndex, newIndex) => {
          const from = elements.indexOf(visibleElements[prevIndex]);
          const to = elements.indexOf(visibleElements[newIndex]);
          if (from >= 0 && to >= 0) {
            dispatch(
              updateConditionChildPositionOperation({
                spreadIndex,
                childrenKey,
                prevIndex: from,
                newIndex: to,
                siblingIndices: visibleElements.map((element) => elements.indexOf(element)),
              }),
            );
          }
        }}
      >
        {visibleElements.map((element) => {
          if (textOnly && element.type !== 'text') {
            return null;
          }

          const key = element.type + element.id;
          switch (element.type) {
            case 'condition':
              return (
                <ReorganizableElement key={key} showBorder={isEditing}>
                  <PersonalizationCondition
                    spreadIndex={spreadIndex}
                    conditionGroup={conditionGroup}
                    condition={conditionGroup.conditions[element.id]}
                    onConditionCreated={onConditionCreated}
                    isBuilding
                    textOnly={textOnly}
                  />
                </ReorganizableElement>
              );
            case 'image':
            case 'addon':
              return (
                <ReorganizableElement key={key} className={styles.element}>
                  <PersonalizationImage isBuilding elementId={element.id} className={className} />
                </ReorganizableElement>
              );
            case 'text':
              return (
                <ReorganizableElement key={key} className={styles.element}>
                  <PersonalizationText isBuilding elementId={element.id} className={className} />
                </ReorganizableElement>
              );
            case 'line':
            case 'rectangle':
              return (
                <ReorganizableElement key={key} className={styles.element}>
                  <PersonalizationShape isBuilding elementId={element.id} className={className} />
                </ReorganizableElement>
              );
            default:
              return null;
          }
        })}
      </ReorganizableList>
    );
  };

  return (
    <>
      {!!elements.length && (
        <FieldGroupSettings
          key={`${spreadIndex}:${parentCondition ? getConditionChildrenKey(parentCondition.id, parentCondition.activeOptionId) : conditionGroupChildenKey}`}
          conditionGroup={conditionGroup}
          spreadIndex={spreadIndex}
          childrenKey={
            parentCondition
              ? getConditionChildrenKey(parentCondition.id, parentCondition.activeOptionId)
              : conditionGroupChildenKey
          }
          elements={elements}
          renderFields={renderFields}
        />
      )}
      {!!ungrouped.length && (
        <div className={styles.listHeader}>
          <h3>{tr('editor-layers', 'Layers')}</h3>
          <span>{ungrouped.length}</span>
        </div>
      )}
      {renderFields(ungrouped)}
    </>
  );
}

export default React.memo(ConditionElements);
